import { effect, inject, Injectable, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import {
  ActivatedRouteSnapshot,
  PRIMARY_OUTLET,
  RouterStateSnapshot,
  TitleStrategy,
} from '@angular/router';
import { PageTitleService } from '@app/infrastructure/routing/page-title.service';
import { LayoutRouteData } from '@app/shared/common/models';

const APP_NAME = 'Comex';
const TITLE_SEPARATOR = ' - ';

@Injectable({ providedIn: 'root' })
export class PageTitleStrategy extends TitleStrategy {
  private readonly title = inject(Title);
  private readonly pageTitleService = inject(PageTitleService);

  private readonly identitySegment = signal<string | undefined>(undefined);
  private readonly routeTabSegments = signal<string[]>([]);

  protected readonly syncDocumentTitleEffect = effect(() => {
    const segments = [
      this.identitySegment(),
      ...this.pageTitleService.tabSegments(),
      ...this.routeTabSegments(),
      APP_NAME,
    ];

    this.title.setTitle(
      segments.filter((segment): segment is string => !!segment).join(TITLE_SEPARATOR),
    );
  });

  override updateTitle(snapshot: RouterStateSnapshot): void {
    this.identitySegment.set(getIdentitySegment(snapshot.root));
    this.routeTabSegments.set(collectTabSegments(snapshot.root));
  }
}

function getIdentitySegment(route: ActivatedRouteSnapshot): string | undefined {
  let identity: string | undefined;
  let current: ActivatedRouteSnapshot | undefined = route;

  while (current) {
    const data = current.routeConfig?.data as LayoutRouteData | undefined;
    identity =
      current.title ?? data?.documentTitle ?? data?.headerLabel ?? data?.breadcrumb ?? identity;
    current = current.children.find((child) => child.outlet === PRIMARY_OUTLET);
  }

  return identity;
}

function collectTabSegments(route: ActivatedRouteSnapshot): string[] {
  const data = route.routeConfig?.data as LayoutRouteData | undefined;
  const segments = data?.pageTitle ? [data.pageTitle] : [];
  const primaryChild = route.children.find((child) => child.outlet === PRIMARY_OUTLET);

  return primaryChild ? [...segments, ...collectTabSegments(primaryChild)] : segments;
}
